import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottokartCustomerEngagementWorkspaceCapability/domain/OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {WorkspaceCapabilityUdoTemplateConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityUdoTemplateConfiguration";
import {z as zod} from "zod";

export type OttokartCustomerEngagementWorkspaceCapabilityUdoBindings = WorkspaceCapabilityUdoTemplateConfiguration<WorkspaceCapabilityType.ottokartCustomerEngagement> & {
    fixedUdoTemplates: {
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.categoryRequest]: {
            udoAdditionalColumnIds: {
                category: Uuid;
                ottokartStorefrontCustomerId: Uuid;
                status: Uuid;
            };
        };
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.fleetProfile]: {
            udoAdditionalColumnIds: {
                address: Uuid;
                approvalStatus: Uuid;
                city: Uuid;
                fleetStatus: Uuid;
                imageUrl: Uuid;
                isNew: Uuid;
                machineName: Uuid;
                machineSpecifications: Uuid;
                machineType: Uuid;
                manufacturer: Uuid;
                modelNo: Uuid;
                ottokartStorefrontCustomerId: Uuid;
                pinCode: Uuid;
                purchaseDate: Uuid;
                serialNo: Uuid;
                state: Uuid;
                status: Uuid;
                telemetryNotes: Uuid;
                vin: Uuid;
            };
        };
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.fleetStatusLog]: {
            udoAdditionalColumnIds: {
                comment: Uuid;
                documents: Uuid;
                fleetId: Uuid;
                images: Uuid;
                logStatus: Uuid;
                status: Uuid;
            };
        };
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.preference]: {
            udoAdditionalColumnIds: {
                ottokartStorefrontCustomerId: Uuid;
                status: Uuid;
                variantIds: Uuid;
            };
        };
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.recentlyViewed]: {
            udoAdditionalColumnIds: {
                displayName: Uuid;
                imageId: Uuid;
                lastViewedAt: Uuid;
                ottokartStorefrontCustomerId: Uuid;
                productId: Uuid;
                sku: Uuid;
                slug: Uuid;
                status: Uuid;
                variantId: Uuid;
            };
        };
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.cartItem]: {
            udoAdditionalColumnIds: {
                displayName: Uuid;
                imageId: Uuid;
                isActive: Uuid;
                ottokartStorefrontCustomerId: Uuid;
                productId: Uuid;
                quantity: Uuid;
                sku: Uuid;
                slug: Uuid;
                status: Uuid;
                variantId: Uuid;
            };
        };
    };
};

export const ottokartCustomerEngagementWorkspaceCapabilityUdoBindingsValidator = zod.strictObject({
    fixedUdoTemplates: zod.strictObject({
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.categoryRequest]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({status: uuidZodValidator, ottokartStorefrontCustomerId: uuidZodValidator, category: uuidZodValidator}),
        }),
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.fleetProfile]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                status: uuidZodValidator,
                ottokartStorefrontCustomerId: uuidZodValidator,
                machineType: uuidZodValidator,
                manufacturer: uuidZodValidator,
                modelNo: uuidZodValidator,
                machineName: uuidZodValidator,
                serialNo: uuidZodValidator,
                vin: uuidZodValidator,
                fleetStatus: uuidZodValidator,
                address: uuidZodValidator,
                city: uuidZodValidator,
                state: uuidZodValidator,
                pinCode: uuidZodValidator,
                purchaseDate: uuidZodValidator,
                imageUrl: uuidZodValidator,
                machineSpecifications: uuidZodValidator,
                telemetryNotes: uuidZodValidator,
                isNew: uuidZodValidator,
                approvalStatus: uuidZodValidator,
            }),
        }),
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.fleetStatusLog]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                status: uuidZodValidator,
                fleetId: uuidZodValidator,
                logStatus: uuidZodValidator,
                images: uuidZodValidator,
                documents: uuidZodValidator,
                comment: uuidZodValidator,
            }),
        }),
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.preference]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({status: uuidZodValidator, ottokartStorefrontCustomerId: uuidZodValidator, variantIds: uuidZodValidator}),
        }),
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.recentlyViewed]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                lastViewedAt: uuidZodValidator,
                status: uuidZodValidator,
                ottokartStorefrontCustomerId: uuidZodValidator,
                productId: uuidZodValidator,
                variantId: uuidZodValidator,
                displayName: uuidZodValidator,
                sku: uuidZodValidator,
                slug: uuidZodValidator,
                imageId: uuidZodValidator,
            }),
        }),
        [OttokartCustomerEngagementWorkspaceCapabilityUdoTemplateKey.cartItem]: zod.strictObject({
            udoTemplateId: uuidZodValidator,
            udoAdditionalColumnIds: zod.strictObject({
                status: uuidZodValidator,
                ottokartStorefrontCustomerId: uuidZodValidator,
                productId: uuidZodValidator,
                variantId: uuidZodValidator,
                displayName: uuidZodValidator,
                sku: uuidZodValidator,
                slug: uuidZodValidator,
                imageId: uuidZodValidator,
                quantity: uuidZodValidator,
                isActive: uuidZodValidator,
            }),
        }),
    }),
}) satisfies zod.ZodType<OttokartCustomerEngagementWorkspaceCapabilityUdoBindings>;
